/* eslint-disable no-unused-vars */
import { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Flex,
    FormControl,
    FormLabel,
    Input,
    Textarea,
    Switch,
    Text,
    Grid,
    GridItem,
    useDisclosure,
    HStack,
} from '@chakra-ui/react';
import CustomSelect from 'components/CustomSelect';
import InputText from 'components/Input';
import SearchableSelect from 'components/SearchableSelect';
import { Backbutton } from 'components/LandingPageComponents/styles/HeaderStyles';
import { backbtn } from '../components/Icons';
import { useNavigate } from 'react-router-dom';
import ReminderModal from './ReminderModal';
import RepeatModal from './RepeatModal';
import { useCustomers, useGetSalesPersons } from 'hooks/useCustomers';
import useDebounce from 'hooks/useDebounce';
import moment from 'moment';
import { useTaskMutations } from 'hooks/useTask';
import toast from 'react-hot-toast';
import useGetUserDetails from 'hooks/useGetUserDetail';

/**
 * CreateTaskForm
 * Full-page form for creating a CRM task.
 * @example <CreateTaskForm />
 */
export function CreateTaskForm() {
    const navigate = useNavigate();

    const priorities = [
        { label: 'Low', value: 'low' },
        { label: 'Medium', value: 'medium' },
        { label: 'High', value: 'high' },
    ];
    const statuses = [
        { label: 'Not Started', value: 'not_started' },
        { label: 'In Progress', value: 'in_progress' },
        { label: 'Completed', value: 'completed' },
    ];
    const user = useGetUserDetails();
    console.log({ user });

    // Form state
    const [form, setForm] = useState({
        owner: '',
        dueDate: '',
        contact: '',
        status: '',
        reminder: false,
        subject: '',
        salesperson: `${
            user?.flattenedUser?.attributes?.first_name ?? user?.flattenedUser?.email ?? ''
        } ${user?.flattenedUser?.attributes?.last_name ?? ''}`,
        priority: 'low',
        description: '',
        repeat: false,
    });
    const [reminderData, setReminderData] = useState({ date: '', time: '', notify: '' });
    const [repeatData, setRepeatData] = useState({ frequency: '' });
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isRepeatOpen, onOpen: onRepeatOpen, onClose: onRepeatClose } = useDisclosure();
    const [reminderModalOpen, setReminderModalOpen] = useState(false);
    const [repeatModalOpen, setRepeatModalOpen] = useState(false);
    const { salespersons, isLoading } = useGetSalesPersons();
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedValue = useDebounce(searchTerm, 700);
    const { fetchCustomers, customers, isLoading: isLoadingCustomer } = useCustomers();
    const { mutate, isPending } = useTaskMutations();

    function handleChange(e) {
        const { name, value, type, checked } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
        if (name === 'reminder') {
            if (!form.reminder && checked) setReminderModalOpen(true);
            if (form.reminder && !checked) setReminderModalOpen(false);
        }
        if (name === 'repeat') {
            if (!form.repeat && checked) setRepeatModalOpen(true);
            if (form.repeat && !checked) setRepeatModalOpen(false);
        }
    }

    function handleSelect(name, value) {
        setForm((prev) => ({ ...prev, [name]: value }));
    }

    function handleReminderChange(e) {
        const { name, value } = e.target;
        setReminderData((prev) => ({ ...prev, [name]: value }));
    }

    function handleRepeatChange(e) {
        const { name, value } = e.target;
        setRepeatData((prev) => ({ ...prev, [name]: value }));
    }

    function handleSetReminder() {
        setReminderModalOpen(false);
    }

    function handleSetRepeat() {
        setRepeatModalOpen(false);
    }

    async function handleSubmit(e) {
        e.preventDefault();
        const payload = {
            data: [
                {
                    description: form?.description,
                    contactName: form?.contact?.contact_name,
                    contactId: form?.contact?.contact_id,
                    dueDate: form?.dueDate,
                    priority: form.priority ? JSON.parse(form?.priority)?.value : 'Low',
                    Subject: form?.subject,
                    recurring: `FREQ=${
                        repeatData?.frequency ? JSON.parse(repeatData?.frequency)?.value : 'DAILY'
                    };INTERVAL=1;COUNT=1;DTSTART=${moment().format('yyyy-MM-DD')};UNTIL=${
                        reminderData?.date
                    }`,
                    status: form?.status ? JSON.parse(form?.status)?.value : 'not_started',
                    owner: form?.owner ? JSON.stringify(JSON.parse(form?.owner)?.Owner) : '',
                },
            ],
        };

        const response = await mutate(payload);
        if (response) {
            navigate('/dashboard/crm?tab=tasks');
            toast.success('task created successfully');
        }
    }

    useEffect(() => {
        fetchCustomers(debouncedValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedValue]);

    // Responsive grid columns

    return (
        <Box as="form" onSubmit={handleSubmit} width={'100%'} mt={8} p={8}>
            <HStack justify="start" alignItems="center" display={'flex'}>
                <Backbutton onClick={() => navigate(-1)}>{backbtn}</Backbutton>
                <Text as="h1" fontSize="2xl" fontWeight="bold" mb={2} color="#1B3B5F">
                    Create Task
                </Text>
            </HStack>

            <Text fontSize="sm" color="gray.500" mb={8}>
                Manage customer relationships from one place
            </Text>

            <Box borderWidth={1} borderColor="#EAECF0" borderRadius="lg" p={8}>
                <Text as="h2" fontWeight="bold" fontSize="lg" mb={6} color="#1B3B5F">
                    Task Information
                </Text>
                <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={6}>
                    {/* Left column */}
                    <GridItem>
                        <FormControl mb={4} isRequired>
                            <FormLabel>Task Owner</FormLabel>
                            <CustomSelect
                                name="owner"
                                options={
                                    salespersons?.map((item) => ({
                                        ...item,
                                        label: `${item?.Name ?? ''} ${item?.Last_Name ?? ''}`,
                                    })) ?? []
                                }
                                placeholder="Choose Task Owner"
                                onChange={(e) => handleSelect('owner', e.target.value)}
                                aria-label="Task Owner"
                            />
                        </FormControl>
                        <FormControl mb={4} isRequired>
                            <FormLabel>Due Date</FormLabel>
                            <Input
                                type="date"
                                name="dueDate"
                                value={form.dueDate}
                                onChange={handleChange}
                                aria-label="Due Date"
                            />
                        </FormControl>
                        <FormControl mb={4} isRequired>
                            <FormLabel>Contact</FormLabel>
                            <SearchableSelect
                                name="contact"
                                searchTerm={searchTerm}
                                setSearchTerm={setSearchTerm}
                                options={
                                    customers?.map((item) => ({
                                        ...item,
                                        contact_name: `${item?.contact_name} (${item?.email})`,
                                    })) ?? []
                                }
                                loading={isLoading}
                                aria-label="Contact"
                                onSelect={(val) => {
                                    handleSelect('contact', val);
                                }}
                            />
                        </FormControl>
                        <FormControl mb={4} isRequired>
                            <FormLabel>Status</FormLabel>
                            <CustomSelect
                                name="status"
                                options={statuses}
                                placeholder="Select Status"
                                value={form.status}
                                onChange={(e) => handleSelect('status', e.target.value)}
                                aria-label="Status"
                            />
                        </FormControl>
                        <FormControl display="flex" alignItems="center" mb={4}>
                            <FormLabel htmlFor="reminder" mb="0">
                                Set Reminder
                            </FormLabel>
                            <Switch
                                id="reminder"
                                name="reminder"
                                isChecked={form.reminder}
                                onChange={handleChange}
                                iconColor="#1b3b5f"
                                aria-label="Set Reminder"
                            />
                        </FormControl>
                    </GridItem>
                    {/* Right column */}
                    <GridItem>
                        <FormControl mb={4} isRequired>
                            <FormLabel>Subject</FormLabel>
                            <InputText
                                name="subject"
                                value={form.subject}
                                onChange={handleChange}
                                placeholder="Enter Subject"
                                aria-label="Subject"
                            />
                        </FormControl>
                        <FormControl mb={4} isRequired>
                            <FormLabel>Salesperson</FormLabel>
                            <InputText
                                name="salesperson"
                                value={form.salesperson}
                                onChange={handleChange}
                                placeholder="Enter Salesperson"
                                aria-label="Salesperson"
                                isReadOnly
                            />
                        </FormControl>
                        <FormControl mb={4} isRequired>
                            <FormLabel>Priority</FormLabel>
                            <CustomSelect
                                name="priority"
                                options={priorities}
                                placeholder="Select Priority"
                                value={form.priority}
                                onChange={(e) => handleSelect('priority', e.target.value)}
                                aria-label="Priority"
                            />
                        </FormControl>
                        <FormControl mb={4}>
                            <FormLabel>Description</FormLabel>
                            <Textarea
                                name="description"
                                value={form.description}
                                onChange={handleChange}
                                placeholder="Enter a description..."
                                aria-label="Description"
                                minH="100px"
                            />
                        </FormControl>
                        <FormControl display="flex" alignItems="center" mb={4}>
                            <FormLabel htmlFor="repeat" mb="0">
                                Set On Repeat
                            </FormLabel>
                            <Switch
                                id="repeat"
                                name="repeat"
                                isChecked={form.repeat}
                                onChange={handleChange}
                                iconColor="#1b3b5f"
                                aria-label="Set On Repeat"
                            />
                        </FormControl>
                    </GridItem>
                </Grid>
                <Flex mt={8} gap={4} justifyContent={{ base: 'center', md: 'flex-start' }}>
                    <Button
                        isLoading={isPending}
                        type="submit"
                        color="white"
                        bg="#1b3b5f"
                        px={8}
                        fontWeight="bold">
                        Create Task
                    </Button>
                    <Button
                        color="#1b3b5f"
                        bg="#B8C1D380"
                        variant="outline"
                        px={8}
                        fontWeight="bold"
                        onClick={() => {}}>
                        Cancel
                    </Button>
                </Flex>
            </Box>

            <ReminderModal
                isOpen={reminderModalOpen}
                onClose={() => setReminderModalOpen(false)}
                value={reminderData}
                onChange={handleReminderChange}
                onSet={handleSetReminder}
            />
            <RepeatModal
                isOpen={repeatModalOpen}
                onClose={() => setRepeatModalOpen(false)}
                value={repeatData}
                onChange={handleRepeatChange}
                onSet={handleSetRepeat}
            />
        </Box>
    );
}
